import type { FC } from 'react';
import { ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';
import type ApiBathCard from 'src/core/models/api/ApiBathCard';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import { clamp } from 'src/core/utils/Numbers';
import type { BathCardVariant } from 'src/domain/models/BathCardVariant';
import CarouselPagination, { FIRST_PAGE } from 'src/presentation/components/molecules/CarouselPagination';
import SectionTitle from 'src/presentation/components/molecules/SectionTitle';
import BathCardCard, { BathCardSkeleton } from 'src/presentation/components/organisms/BathCardCard';
import { CAROUSEL_CARD_GAP, useCarouselPager } from 'src/presentation/hooks/useCarouselPager';

const CARD_HORIZONTAL_INSET = Dimens.padding18 * 2;
const SINGLE_CARD = 1;

interface Props {
	cards: ApiBathCard[];
	page: number;
	setPage: (page: number) => void;
	variant: BathCardVariant;
	title?: string;
	showHeader?: boolean;
	skeletonCount?: number;
	style?: StyleProp<ViewStyle>;
	onPressView?: (card: ApiBathCard) => void;
	onPressReserve: (card: ApiBathCard) => void;
	onPressBuy?: (card: ApiBathCard) => void;
	onPressRemove: (card: ApiBathCard) => void;
}

const BathCardsSlider: FC<Props> = ({
	cards,
	page,
	setPage,
	variant,
	title = Translations.bathcardCards,
	showHeader = true,
	skeletonCount = 0,
	style,
	onPressView,
	onPressReserve,
	onPressBuy,
	onPressRemove,
}) => {
	const { width: windowWidth } = useWindowDimensions();

	const placeholderCount = cards.length === 0 ? skeletonCount : 0;
	const pageCount = Math.max(cards.length, placeholderCount);
	const cardWidth =
		pageCount === SINGLE_CARD
			? windowWidth - CARD_HORIZONTAL_INSET
			: clamp(windowWidth - Dimens.carouselPeek80, Dimens.size275, Dimens.size380);
	const pager = useCarouselPager(cardWidth, pageCount, setPage);

	if (pageCount === 0) {
		return null;
	}

	return (
		<View style={[styles.section, style]}>
			{showHeader ? (
				<View style={styles.header}>
					<SectionTitle title={title} underline={false} />
					<CarouselPagination
						page={clamp(page, FIRST_PAGE, pageCount)}
						pageCount={pageCount}
						onPageChange={pager.goToPage}
					/>
				</View>
			) : null}

			<ScrollView
				contentContainerStyle={styles.track}
				horizontal={true}
				ref={pager.ref}
				scrollEventThrottle={20}
				showsHorizontalScrollIndicator={false}
				snapToAlignment="start"
				snapToInterval={pager.snapInterval}
				style={styles.trackBleed}
				{...pager.scrollHandlers}
			>
				{Array.from({ length: placeholderCount }, (_, index) => (
					<BathCardSkeleton key={index} width={cardWidth} />
				))}

				{cards.map((card) => (
					<BathCardCard
						card={card}
						key={card.id}
						variant={variant}
						width={cardWidth}
						onPressRemove={() => onPressRemove(card)}
						onPressReserve={() => onPressReserve(card)}
						{...(onPressBuy ? { onPressBuy: () => onPressBuy(card) } : {})}
						{...(onPressView ? { onPressView: () => onPressView(card) } : {})}
					/>
				))}
			</ScrollView>
		</View>
	);
};

const styles = StyleSheet.create({
	section: {
		gap: Dimens.gap16,
	},
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingHorizontal: Dimens.padding18,
	},
	track: {
		gap: CAROUSEL_CARD_GAP,
		paddingHorizontal: Dimens.padding18,
		paddingVertical: Dimens.padding24,
	},
	trackBleed: {
		marginVertical: Dimens.paddingMinus24,
	},
});

export default BathCardsSlider;
